import { Component } from 'react';
import { connect } from 'react-redux';
import DrawStageContainer from '../draw-stage/draw-stage-container';
import DoodleStage from '../doodle-stage/doodle-stage';
import {
  selectCharacter,
  changeLanguage,
} from 'state/action-creators/characters';
import {
  toggleShowControls,
  toggleDrawInProgress,
  resetPathIndex,
  toggleDrawFinished,
  toggleShowBackground,
  toggleShowLines,
  audioDownloadRequest,
  audioDownloadResponse,
  setNextPathIndex,
  enableContinuousDraw,
  displayStartButton,
} from 'state/action-creators/draw-stage';
import { CharacterCase, CharacterType, Language, Locale } from 'types/enums';
import ButtonOverlay from '../button-overlay';
import browser from 'utils/browser';
import Box from '@material-ui/core/Box';
import { setLocale } from 'translations/translator';

class DrawPage extends Component<any> {
  componentDidMount() {
    this.props.onDisplayStartButton(
      this.props.isLite || browser() === 'safari'
    );
    this.selectCharacterFromQueryString();
  }

  componentDidUpdate(prevProps) {
    if (this.nextPropsWillChange(prevProps)) {
      this.selectCharacterFromQueryString();
    }
  }
  nextPropsWillChange(prevProps) {
    return (
      prevProps.characterType !== this.props.characterType ||
      prevProps.characterCase !== this.props.characterCase ||
      prevProps.character !== this.props.character
    );
  }
  selectCharacterFromQueryString() {
    const { characterType, characterCase, character } = this.props;

    if (
      this.props.queryObject &&
      (this.props.queryObject.languageCode !== undefined || this.props.queryObject.locale !== undefined)
    ) {
      const languageId = this.getLanguageIdFromLanguageCode(
        this.props.queryObject.languageCode,
        this.props.queryObject.locale
      );

      setLocale(languageId);
      this.props.onChangeLanguage(languageId);
    }

    this.props.onSelectCharacter(characterType, characterCase, character);

    this.props.onToggleShowControls(true);
    this.props.onToggleDrawFinished(false);
    this.props.onResetPathIndex();

    this.handleLinesAndBackgroundToggling();
  }

  getLanguageIdFromLanguageCode(languageCode, locale = '') {
    if (locale) {
      return locale === Locale.SPANISH ? Language.SPANISH : Language.ENGLISH
    }

    if (typeof languageCode === 'string') {
      return languageCode.toLowerCase() === 'es'
        ? Language.SPANISH
        : Language.ENGLISH;
    }

    return Language.ENGLISH;
  }

  componentWillUnmount() {
    this.props.onToggleDrawInProgress(false);
    this.props.onToggleShowControls(false);
  }

  handleLinesAndBackgroundToggling = () => {
    if (
      this.props.characterCase === CharacterCase.LOWERCASE ||
      this.props.characterType === CharacterType.CURSIVE
    ) {
      if (this.props.showBackground) {
        this.props.onToggleShowBackground();
      }
    } else {
      if (!this.props.showBackground) {
        this.props.onToggleShowBackground();
      }

      if (!this.props.showLines) {
        this.props.onToggleShowLines();
      }
    }
  };

  handleAutoplay() {
    if (this.props.isLite && !this.props.showStartButton) {
      this.props.onEnableContinuousDraw();
    }
  }

  handleAudioDownloadResponse = () => {
    this.props.onAudioDownloadResponse();
    this.props.onEnableContinuousDraw();
  };

  handleStartGame = () => {
    this.props.onDisplayStartButton(false);
  };

  render() {
    const pencilActive = this.props.pencilActive;
    return (
      <Box
        className='draw-page'
        tabIndex={0}
        style={pencilActive ? { overflow: 'hidden' } : { overflow: 'auto' }}
      >
        <ButtonOverlay
          show={this.props.showStartButton}
          onClick={this.handleStartGame}
        />
        <DoodleStage
          extraHeight={true}
          pencilEnabled={this.props.pencilActive}
          eraserEnabled={this.props.eraserActive}
          eraseAll={this.props.eraseAll}
          zoomLevel={this.props.zoomLevel}
        />
        <DrawStageContainer
          character={this.props.character}
          characterCase={this.props.characterCase}
          currentPathIndex={this.props.currentPathIndex}
          characterType={this.props.characterType}
          svgPath={this.props.svgPath}
          showBackground={this.props.showBackground}
          showLines={this.props.showLines}
          languageId={this.props.languageId}
          audioLoading={this.props.audioLoading}
          gameStarted={!this.props.showStartButton}
          zoomLevel={this.props.zoomLevel}
          volume={this.props.volume}
          muted={this.props.muted}
          textSize={this.props.textSize}
          onSetNextPathIndex={this.props.onSetNextPathIndex}
          onToggleDrawInProgress={this.props.onToggleDrawInProgress}
          onToggleDrawFinished={this.props.onToggleDrawFinished}
          onAudioDownloadRequest={this.props.onAudioDownloadRequest}
          onAudioDownloadResponse={this.handleAudioDownloadResponse}
        />
      </Box>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    showBackground: state.drawStage.showBackground,
    showLines: state.drawStage.showLines,
    currentPathIndex: state.drawStage.currentPathIndex,
    drawFinished: state.drawStage.drawFinished,
    audioLoading: state.drawStage.audioLoading,
    showStartButton: state.drawStage.showStartButton,
    svgPath: state.characters.selectedCharacterItem.svgPath,
    languageId: state.characters.languageId,
    zoomLevel: state.characters.zoomLevel,
    textSize: state.characters.textSize,
    volume: state.characters.volumeLevel,
    muted: state.characters.muted,
    pencilActive: state.doodleStage.pencilActive,
    eraserActive: state.doodleStage.eraserActive,
    eraseAll: state.doodleStage.eraseAll,
  };
};

export default connect(mapStateToProps, {
  onSelectCharacter: selectCharacter,
  onToggleShowControls: toggleShowControls,
  onToggleDrawInProgress: toggleDrawInProgress,
  onResetPathIndex: resetPathIndex,
  onToggleDrawFinished: toggleDrawFinished,
  onToggleShowBackground: toggleShowBackground,
  onToggleShowLines: toggleShowLines,
  onAudioDownloadRequest: audioDownloadRequest,
  onAudioDownloadResponse: audioDownloadResponse,
  onSetNextPathIndex: setNextPathIndex,
  onEnableContinuousDraw: enableContinuousDraw,
  onChangeLanguage: changeLanguage,
  onDisplayStartButton: displayStartButton,
})(DrawPage);
